import {
  Box,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Heading,
  HStack,
  SimpleGrid,
} from "@chakra-ui/react";
import { HiOutlinePlus, HiOutlineTrash } from "react-icons/hi";
import CodesetSelect from "../../CodesetSelect";
import UuidPreview from "../../UuidPreview";
import CurrencyAmountInput from "../../CurrencyAmountInput";
import RateAmountInput from "../../RateAmountInput";
import PartySelectById from "../../PartySelectById";
import { createEmptyMarket } from "../../utils/placementDefaults";

const MarketItemEditor = ({ market, marketIndex, onUpdate, onRemove }) => {
  const updateField = (field, value) => {
    onUpdate(marketIndex, field, value);
  };

  return (
    <Box borderWidth="1px" borderRadius="md" p={4} mb={6} borderColor="gray.500">
      <UuidPreview uuid={market.id} />

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
        <PartySelectById
          label="Party Reference"
          value={market.partyReference}
          onChange={(value) => updateField("partyReference", value)}
        />

        <CodesetSelect
          label="Role"
          codesetCode="7502"
          value={market.role ? { code: market.role, description: "" } : null}
          onChange={(value) => updateField("role", value.code)}
        />

        <FormControl>
          <FormLabel>Role Description</FormLabel>
          <Input
            value={market.roleDescription || ""}
            onChange={(e) => updateField("roleDescription", e.target.value)}
          />
        </FormControl>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4} mb={4}>
        <RateAmountInput
          label="Share %"
          value={market.share}
          onChange={(value) => updateField("share", value)}
        />

        <RateAmountInput
          label="Written Line %"
          value={market.writtenLinePercentage}
          onChange={(value) => updateField("writtenLinePercentage", value)}
        />

        <RateAmountInput
          label="Signed Line %"
          value={market.signedLinePercentage}
          onChange={(value) => updateField("signedLinePercentage", value)}
        />

        <RateAmountInput
          label="Order %"
          value={market.orderPercentage}
          onChange={(value) => updateField("orderPercentage", value)}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
        <RateAmountInput
          label="Broker %"
          value={market.brokerPercentage}
          onChange={(value) => updateField("brokerPercentage", value)}
        />

        <CurrencyAmountInput
          label="Written Line Amount"
          value={market.writtenLineAmount}
          onChange={(value) => updateField("writtenLineAmount", value)}
        />

        <CurrencyAmountInput
          label="Signed Line Amount"
          value={market.signedLineAmount}
          onChange={(value) => updateField("signedLineAmount", value)}
        />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
        <CodesetSelect
          label="Status"
          codesetCode="7503"
          value={market.statusCode ? { code: market.statusCode, description: "" } : null}
          onChange={(value) => updateField("statusCode", value.code)}
        />

        <FormControl>
          <FormLabel>Carrier Reference</FormLabel>
          <Input
            value={market.carrierReference || ""}
            onChange={(e) => updateField("carrierReference", e.target.value)}
          />
        </FormControl>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
        <PartySelectById
          label="Underwriter Party"
          value={market.underwriterParty}
          onChange={(value) => updateField("underwriterParty", value)}
        />

        <PartySelectById
          label="Actuary"
          value={market.actuary}
          onChange={(value) => updateField("actuary", value)}
        />

        <PartySelectById
          label="Contract Signatory"
          value={market.contractSignatory}
          onChange={(value) => updateField("contractSignatory", value)}
        />
      </SimpleGrid>

      <HStack justify="flex-end" mt={4}>
        <IconButton
          size="md"
          icon={<HiOutlineTrash />}
          aria-label="Delete Market"
          onClick={() => onRemove(marketIndex)}
        />
      </HStack>
    </Box>
  );
};

const ContractMarketEditor = ({ contractMarket = [], onChange }) => {
  const updateMarket = (marketIndex, field, value) => {
    const updated = [...contractMarket];
    updated[marketIndex] = {
      ...updated[marketIndex],
      [field]: value,
    };
    onChange(updated);
  };

  const addMarket = () => {
    onChange([...contractMarket, createEmptyMarket()]);
  };

  const removeMarket = (marketIndex) => {
    const updated = [...contractMarket];
    updated.splice(marketIndex, 1);
    onChange(updated);
  };

  return (
    <Box>
      <HStack alignItems="center" mb={4}>
        <Heading size="md">Contract Market</Heading>
        <IconButton
          icon={<HiOutlinePlus />}
          size="md"
          aria-label="Add Market"
          onClick={addMarket}
        />
      </HStack>

      {contractMarket.map((market, marketIndex) => (
        <MarketItemEditor
          key={market.id}
          market={market}
          marketIndex={marketIndex}
          onUpdate={updateMarket}
          onRemove={removeMarket}
        />
      ))}
    </Box>
  );
};

export default ContractMarketEditor;
